import React, { memo, useMemo } from 'react'
import compact from 'lodash/compact'
import Card from 'transfix-ui/components/Card'
import { Skeleton } from 'transfix-ui/core'
import Typography from 'transfix-ui/core/Typography'
import { makeStyles } from 'transfix-ui/core/styles'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { HOMEPAGE_BOX_SHADOW, TEST_IDS } from '../../constants'
import ActiveShipmentsInternalCountIndicator from './components/ActiveShipmentsInternalCountIndicator'
import ActiveShipmentsMap from './components/ActiveShipmentsMap'
import ActiveShipmentsSummaryCard from './components/ActiveShipmentsSummaryCard'
import { IActiveShipmentFragment } from './graphql/ActiveShipmentFragment'
import { useActiveShipmentsQuery } from './graphql/ActiveShipments'

const useActiveShipmentsSectionStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    height: '100%',
  },
  flex: {
    [theme.breakpoints.up('md')]: {
      flexGrow: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
    },
    [theme.breakpoints.up('lg')]: {
      flexDirection: 'row',
    },
  },
  mapWrapper: {
    display: 'none',
    minHeight: 260,

    [theme.breakpoints.up('md')]: {
      display: 'block',
      flex: `0 0 300px`,
    },

    [theme.breakpoints.up('lg')]: {
      flex: `1 0 auto`,
    },
  },
  cardWrapper: {
    [theme.breakpoints.up('lg')]: {
      flex: '0 0 300px',
    },
  },
  skeletonCard: {
    height: '100%',
    '& .card-content, & .mapSkeleton': {
      height: '100%',
    },
  },
  map: {
    height: '100%',
    position: 'relative',
    borderRadius: theme.spacing(0.5),
    boxShadow: HOMEPAGE_BOX_SHADOW,
    overflow: 'hidden',
  },
  internalCount: {
    position: 'absolute',
    left: theme.spacing(2),
    bottom: theme.spacing(2),
    zIndex: 2,
  },
}))

const ActiveShipmentsSection = memo(() => {
  const classes = useActiveShipmentsSectionStyles()
  const { isMobile } = useBreakpointContext()

  const { data, loading } = useActiveShipmentsQuery({ fetchPolicy: 'cache-and-network' })
  const { shipments } = data || {}
  const { nodes = [], trackingStatusCount, totalCount = 0 } = shipments || {}
  const { notTracking = 0, onTime = 0, runningLate = 0 } = trackingStatusCount || {}
  const activeShipments = compact<IActiveShipmentFragment>(nodes)

  const showCard = useMemo(() => {
    if (loading || isMobile) {
      return true
    }
    return !loading && totalCount > 0
  }, [isMobile, loading, totalCount])

  return (
    <div data-test={TEST_IDS.activeShipmentsSection} className={classes.container}>
      <Typography variant='h3'>Load Summary</Typography>
      <div className={classes.flex}>
        <div className={classes.mapWrapper}>
          {loading ? (
            <Card noPadding className={classes.skeletonCard}>
              <Skeleton variant='rectangular' className='mapSkeleton' />
            </Card>
          ) : (
            <div className={classes.map}>
              <div className={classes.internalCount}>
                <ActiveShipmentsInternalCountIndicator />
              </div>
              <ActiveShipmentsMap activeShipments={activeShipments} />
            </div>
          )}
        </div>
        {showCard && (
          <div className={classes.cardWrapper}>
            <ActiveShipmentsSummaryCard
              loading={loading}
              activeLoadCount={totalCount}
              notTrackingCount={notTracking}
              onTimeCount={onTime}
              runningLateCount={runningLate}
            />
          </div>
        )}
      </div>
    </div>
  )
})

export default ActiveShipmentsSection
